import favicon from "../../assets/favicon.svg";

const SITE = "https://dojotrack.com";

type AuthSiteFooterProps = {
  appBaseUrl: string;
};

const socialIconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export default function AuthSiteFooter({ appBaseUrl }: AuthSiteFooterProps) {
  const openCookieSettings = () => {
    window.dispatchEvent(new Event("dojotrack-open-cookie-settings"));
  };

  return (
    <footer className="auth-site-footer">
      <div className="auth-footer__grid">
        <div>
          <a href={SITE} className="auth-footer__logo">
            <img
              className="auth-footer__mark"
              src={favicon}
              alt=""
              width={50}
              height={50}
            />
            <span className="auth-logo__word">Dojotrack</span>
            <span className="sr-only">DojoTrack</span>
          </a>
          <p className="auth-footer__mission">
            Our mission is to build martial arts software that fits how studios
            really run — so staff spends less time on admin work and missed
            payments, and more time with students on the mat.
          </p>
          <nav className="auth-footer__socials" aria-label="Social media">
            <a
              href="https://facebook.com/dojotrackapp/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="sr-only">Facebook</span>
              <svg {...socialIconProps}>
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3Z" />
              </svg>
            </a>
            <a href="https://x.com/dojotrack" target="_blank" rel="noopener noreferrer">
              <span className="sr-only">X</span>
              <svg {...socialIconProps}>
                <path d="M4 4l11.7 16h4.3L8.3 4H4zm0 16 6.7-7.6M13.3 11.6 20 4" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/dojotrack/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="sr-only">Instagram</span>
              <svg {...socialIconProps}>
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r=".8" fill="currentColor" stroke="none" />
              </svg>
            </a>
            <a
              href="https://linkedin.com/company/dojotrackapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="sr-only">LinkedIn</span>
              <svg {...socialIconProps}>
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
                <circle cx="4" cy="4" r="2" />
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@DojoTrack"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="sr-only">YouTube</span>
              <svg {...socialIconProps}>
                <path d="M22.5 6.5s-.2-1.6-.8-2.3c-.8-.8-1.7-.8-2.1-.9C16.9 3 12 3 12 3s-4.9 0-7.6.3c-.4.1-1.3.1-2.1.9C1.7 4.9 1.5 6.5 1.5 6.5S1.3 8.4 1.3 10.3v1.4c0 1.9.2 3.8.2 3.8s.2 1.6.8 2.3c.8.8 1.9.8 2.4.9 1.7.1 7.3.2 7.3.2s4.9 0 7.6-.3c.4-.1 1.3-.1 2.1-.9.6-.7.8-2.3.8-2.3s.2-1.9.2-3.8v-1.4c0-1.9-.2-3.8-.2-3.8Z" />
                <polygon points="9.8 14.5 15.5 11.2 9.8 7.9 9.8 14.5" fill="currentColor" stroke="none" />
              </svg>
            </a>
          </nav>
        </div>

        <div>
          <h2>Product</h2>
          <ul>
            <li>
              <a href={`${SITE}/product-demo/`}>Watch demo</a>
            </li>
            <li>
              <a href={`${SITE}/free-plan/`}>Free features</a>
            </li>
            <li>
              <a href={`${SITE}/paid-plans/`}>Premium features</a>
            </li>
            <li>
              <a href={`${SITE}/pricing/`}>Pricing</a>
            </li>
            <li>
              <a href={`${appBaseUrl}/signup`}>Start free</a>
            </li>
            <li>
              <a href={`${appBaseUrl}/login`}>Log In</a>
            </li>
          </ul>
        </div>

        <div>
          <h2>Company</h2>
          <ul>
            <li>
              <a href={`${SITE}/about/`}>About</a>
            </li>
            <li>
              <a href={`${SITE}/blog/`}>Blog</a>
            </li>
            <li>
              <a href={`${SITE}/contact/`}>Contact</a>
            </li>
            <li>
              <a href={`${SITE}/terms/`}>Terms</a>
            </li>
            <li>
              <a href={`${SITE}/privacy-policy/`}>Privacy</a>
            </li>
            <li>
              <a href={`${SITE}/cookie-policy/`}>Cookies</a>
            </li>
          </ul>
        </div>

        <div>
          <h2>Contact</h2>
          <ul>
            <li>
              <a href="mailto:support@dojotrack.com">support@dojotrack.com</a>
            </li>
            <li>Utah, USA</li>
          </ul>
        </div>
      </div>

      <div className="auth-footer__base">
        <p>
          © {new Date().getFullYear()} DojoTrack LLC. All rights reserved.{" "}
          <span aria-hidden="true">·</span>{" "}
          <a href={`${SITE}/terms/`}>Terms</a>{" "}
          <span aria-hidden="true">·</span>{" "}
          <a href={`${SITE}/privacy-policy/`}>Privacy</a>{" "}
          <span aria-hidden="true">·</span>{" "}
          <a href={`${SITE}/cookie-policy/`}>Cookies</a>{" "}
          <span aria-hidden="true">·</span>{" "}
          <button
            type="button"
            className="auth-footer__cookie-settings"
            onClick={openCookieSettings}
          >
            Cookie Settings
          </button>
        </p>
        <p>Martial arts CRM software for studios in the USA</p>
      </div>
    </footer>
  );
}
